import i18n from '@ConfigLoader/I18n';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';

// STYES
import styles from '@UILayer/UILayer.scss';

// MANAGERS
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import {
    getAuthenticateGameConfig,
    getBetState,
    getDeviceInfo,
    getEnvironmentConfiguration,
    getGlobalStyles,
    getNumberOfReels,
    getNumberOfSymbolsPerReel
} from '@Managers/Redux/AppState/AppState';
import { useAppSelector } from '@Managers/Redux/Hooks';

//TYPES
import { EConfigurationNames, IHelpItem } from '@ConfigLoader/ConfigFiles';
import { useTranslation } from 'react-i18next';
import { getImagePathResolver } from './Help.imagePathResolver';
import { getImageStylesResolver } from './Help.imageStylesResolver';
import { textParser } from './Help.textParser';

interface IHelpState {
    data: Array<{ content: IHelpItem; key: string }>;
    dataByKey: { [key: string]: string };
    valuePlaceholders: { [key: string]: string };
}

const Help: React.FC<{}> = () => {
    const [state, setState] = useReducer((state: IHelpState, newState: IHelpState) => ({ ...state, ...newState }), {
        data: [],
        dataByKey: {},
        valuePlaceholders: {}
    });

    const globalStyles = useAppSelector(getGlobalStyles);
    const gameStoreBetState = useAppSelector(getBetState);
    const gameStoreAuthenticateGameConfig = useAppSelector(getAuthenticateGameConfig);
    const gameStoreNumberOfReels = useAppSelector(getNumberOfReels);
    const gameStoreNumberOfSymbolsPerReel = useAppSelector(getNumberOfSymbolsPerReel);
    const gameStoreEnvironmentConfiguration = useAppSelector(getEnvironmentConfiguration);
    const device = useAppSelector(getDeviceInfo);
    const { t } = useTranslation();

    useEffect(() => {
        const contentKeys = ConfigLoader.getConfiguration(EConfigurationNames.GAME).helpContentKeys;
        const translationPack = i18n.getDataByLanguage(ConfigLoader.language)?.translation as any;
        const content = translationPack && translationPack['HELP_CONTENT'] ? translationPack['HELP_CONTENT'] : {};
        const contentByKey = translationPack && translationPack['TEXT_BY_KEY'] ? translationPack['TEXT_BY_KEY'] : {};
        const betData = gameStoreBetState;
        const data = [];
        const excludeContent = ConfigLoader.getConfiguration(EConfigurationNames.GAME).helpContentKeysExcluded;
        const jackpotCasinoImagesPath = `${ConfigLoader.sharedResourcePath}/bitmaps/help/jackpotCasino`;
        const cacheVersion = `${ConfigLoader.getConfiguration(EConfigurationNames.VERSION).cacheVersion}`;

        if (!ConfigLoader.getConfiguration(EConfigurationNames.UI_LAYER).wcaMode) excludeContent.push('WCA');
        if (gameStoreAuthenticateGameConfig.gamble.maxTries === 0) excludeContent.push('GAMBLE', 'GAMBLE_WIN_RATE');
        if (!gameStoreAuthenticateGameConfig.bonusBuy) excludeContent.push('BONUS_BUY');
        if (!ConfigLoader.getConfiguration(EConfigurationNames.GAME).dynamicCasinoJackpot.available) excludeContent.push('JACKPOT_CASINO');

        for (var i = 0; i < contentKeys.length; i++) {
            if (content[contentKeys[i]]) {
                const key = contentKeys[i];

                if (!excludeContent.includes(key)) data.push({ content: content[key] as unknown as IHelpItem, key: key });
            }
        }

        let winlines = gameStoreAuthenticateGameConfig.payLines.length;

        if (gameStoreAuthenticateGameConfig.powerLines) {
            winlines = Math.pow(gameStoreNumberOfSymbolsPerReel, gameStoreNumberOfReels);
        }

        setState({
            data: data,
            dataByKey: contentByKey,
            valuePlaceholders: {
                '%game_name': ConfigLoader.getConfiguration(EConfigurationNames.GAME).gameTitle,
                '%reels_no': `${gameStoreNumberOfReels}`,
                '%winline_number': `${winlines}`,
                '%bet_min': `${betData.betSteps[0].formatted}`,
                '%bet_max': `${betData.betSteps[betData.betSteps.length - 1].formatted}`,
                '%game_rtp': `${gameStoreAuthenticateGameConfig.rtp}`,
                '%gamble_options': t('GAMBLE_OPTIONS_DESCRIPTION'),
                '%gamble_max_value': `${gameStoreAuthenticateGameConfig.gamble.maxWin.formatted}`,
                '%gamble_max_tries': `${gameStoreAuthenticateGameConfig.gamble.maxTries}`,
                '%gamble_half_min_credits': `${gameStoreAuthenticateGameConfig.gamble.halfMinValue.formatted}`,
                '%gamble_half_max_tries': `${gameStoreAuthenticateGameConfig.gamble.halfTimes}`,
                '%allowed_auto_spins': `${gameStoreEnvironmentConfiguration.autoPlaySettings.spinValues.toString().replaceAll(',', ', ')}`,
                '%jp_casino_img_diamond': `${jackpotCasinoImagesPath}/jp_label_diamond.webp?v=${cacheVersion}`,
                '%jp_casino_img_gold': `${jackpotCasinoImagesPath}/jp_label_gold.webp?v=${cacheVersion}`,
                '%jp_casino_img_silver': `${jackpotCasinoImagesPath}/jp_label_silver.webp?v=${cacheVersion}`,
                '%jp_casino_img_bronze': `${jackpotCasinoImagesPath}/jp_label_bronze.webp?v=${cacheVersion}`
            }
        });
    }, []);

    const containerCN = cn(styles.settings_content_help, globalStyles.orientation, globalStyles.screenSize, globalStyles.isTablet, globalStyles.wideDevice);

    return (
        <div className={containerCN} role="table">
            {state.data.map((helpItem: { content: IHelpItem; key: string }, index: any) => {
                return (
                    <div
                        id={`help_${helpItem.key.toLowerCase()}`}
                        key={index}
                        className={cn(styles.help_item, globalStyles.orientation)}
                        role="rowgroup"
                        tabIndex={0}
                    >
                        <div id={`help_${helpItem.key.toLowerCase()}_label`} className={styles.help_item__left_col}>
                            {helpItem.content.item.text && helpItem.content.item.text}
                            {helpItem.content.item.image && (
                                <img
                                    className={styles.help_item__left_col_image}
                                    src={getImagePathResolver(helpItem.content.item.image)}
                                    alt={helpItem.content.item.image.alt}
                                    style={getImageStylesResolver(helpItem.content.item.image, device)}
                                />
                            )}
                        </div>
                        <div className={styles.help_item__right_col}>
                            {helpItem.content.description && (
                                <>
                                    {textParser(
                                        helpItem.content.description,
                                        {
                                            single: helpItem.content.description.image,
                                            array: helpItem.content.description.imageArray
                                        },
                                        state.dataByKey,
                                        state.valuePlaceholders
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Help;
